import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { signInWithPassword, sendAuthCode } from '../../redux/modules/auth';
import { trackEvent } from '../../helpers/Analytics';
import {
  getBuyerLoginPath,
  getSellerLoginPath,
} from 'helpers/clientOnboarding';
import { getLastAuth, setLastAuth, LAST_AUTH_METHODS } from 'helpers/lastAuth';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import {
  AuthShell,
  OrDivider,
  GoogleContinueButton,
  authStyles as styles,
} from 'components/Auth';

const SignIn = ({ location, signInWithPassword, sendAuthCode, auth }) => {
  // Don't read localStorage during render — it mismatches SSR and breaks
  // hydration (toggle clicks + last-used badge).
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [lastUsedMethod, setLastUsedMethod] = useState(null);
  const [emailPrefilled, setEmailPrefilled] = useState(false);
  // Request-local email/username for success handling (also persisted via lastAuth).
  const submittedEmailRef = useRef('');
  const isSubmittingRef = useRef(false);

  // Extract referer and tw_user_resolved from URL search params
  const { referer, twUserResolved } = (() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      return {
        referer: params.get('referer') || '',
        twUserResolved: params.get('tw_user_resolved') === 'true',
      };
    }
    return {
      referer: location.query.referer || '',
      twUserResolved: location.query.tw_user_resolved === 'true',
    };
  })();

  const primaryLastUsed =
    (lastUsedMethod === LAST_AUTH_METHODS.PASSWORD && showPassword) ||
    (lastUsedMethod === LAST_AUTH_METHODS.CODE && !showPassword);
  const googleLastUsed = lastUsedMethod === LAST_AUTH_METHODS.GOOGLE;

  // Function to handle and format error messages
  const handleError = (error, errorType) => {
    isSubmittingRef.current = false;
    setIsLoading(false);
    setIsSuccess(false);

    let message = '';

    // Track the error based on type
    if (errorType === 'password') {
      // For password errors
      message = error;
      trackEvent('signin-password-error', {
        error: message,
        referer,
      });
    } else if (errorType === 'passwordless') {
      // For passwordless errors
      // Extract error status from various possible formats
      const errorStatus =
        (error && error.status_code) ||
        (error && error.status) ||
        auth.errorStatus;

      const isEmailError = errorStatus === 422 || errorStatus === 404;
      const isRateLimited = errorStatus === 429;

      if (isEmailError) {
        message =
          'Please check that your email address is correct and try again.';
      } else if (isRateLimited) {
        message =
          'You have recently requested a sign-in code, please wait 60 seconds and try again.';
      } else {
        message = 'Something went wrong. Please try again later.';
      }

      trackEvent('signin-passwordless-error', {
        error: message,
        errorCode:
          (error && error.status_code) || auth.errorStatus || 'unknown',
        referer,
      });
    }

    setErrorMessage(message);
  };

  useEffect(() => {
    const hydrateLastAuth = () => {
      const lastAuth = getLastAuth();
      if (!lastAuth || !lastAuth.method) {
        return;
      }

      setLastUsedMethod(lastAuth.method);
      setShowPassword(lastAuth.method === LAST_AUTH_METHODS.PASSWORD);
      if (
        lastAuth.email &&
        (lastAuth.method === LAST_AUTH_METHODS.PASSWORD ||
          lastAuth.method === LAST_AUTH_METHODS.CODE)
      ) {
        setEmail(lastAuth.email);
        setEmailPrefilled(true);
      }
    };

    hydrateLastAuth();

    // bfcache restores skip mount effects; re-read so the badge can appear.
    const onPageShow = (event) => {
      if (event.persisted) {
        hydrateLastAuth();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Track page view when component mounts
  useEffect(() => {
    trackEvent('user-lands-on-signin', { referer });
  }, []);

  // Handle password sign-in errors
  useEffect(() => {
    if (auth.passwordSignInError) {
      handleError(auth.passwordSignInError, 'password');
    }
  }, [auth.passwordSignInError]);

  // Track successful password sign-in - separate useEffect
  useEffect(() => {
    if (auth.passwordSignInResponse) {
      const data = auth.passwordSignInResponse;

      // Persist immediately so a later redirect/unmount cannot skip it.
      setLastAuth({
        method: LAST_AUTH_METHODS.PASSWORD,
        email: submittedEmailRef.current,
      });

      // Set success state
      setIsLoading(false);
      setIsSuccess(true);

      // Track successful password sign-in
      trackEvent('signin-password-success', {
        userType: data?.type || 'unknown',
        referer,
      });

      // Add a small delay to show success state before redirecting
      setTimeout(() => {
        // Re-write immediately before navigation in case of intervening updates.
        setLastAuth({
          method: LAST_AUTH_METHODS.PASSWORD,
          email: submittedEmailRef.current,
        });

        // Redirect based on user type
        window.location =
          data && data.type
            ? data.type === 'seller'
              ? referer || getSellerLoginPath()
              : data.type === 'buyer'
              ? referer || getBuyerLoginPath()
              : referer || '/' // Fallback if type is unexpected
            : referer || '/'; // Fallback if type is not present
      }, 1000);
    }
  }, [auth.passwordSignInResponse, referer]);

  // Handle passwordless auth errors
  useEffect(() => {
    if (auth.error) {
      handleError(auth.error, 'passwordless');
    }
  }, [auth.error]);

  // Handle successful auth code creation - separate useEffect
  useEffect(() => {
    if (auth.authCodeResponse) {
      const submittedEmail = submittedEmailRef.current;

      // Set success state
      setIsLoading(false);
      setIsSuccess(true);

      // Track successful auth code request
      trackEvent('signin-passwordless-auth-code-sent', { referer });

      // Add a small delay to show success state before redirecting
      setTimeout(() => {
        // Redirect to the code entry page with base64 encoded email
        const base64Email =
          typeof window !== 'undefined'
            ? btoa(submittedEmail)
            : Buffer.from(submittedEmail).toString('base64');

        window.location = `/auth/code?email=${base64Email}&referer=${encodeURIComponent(
          referer || 'signin'
        )}${twUserResolved ? '&tw_user_resolved=true' : ''}`;
      }, 1000);
    }
  }, [auth.authCodeResponse, referer, twUserResolved]);

  const trackMethodChosen = (method) => {
    trackEvent('signin-method-chosen', {
      method,
      lastUsedShown: lastUsedMethod === method,
      emailPrefilled,
      referer,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Guard against overlapping submits from Enter keydown / button / form.
    if (isSubmittingRef.current || isLoading || isSuccess) {
      return;
    }

    // Clear any previous errors
    setErrorMessage('');

    if (!email) {
      return;
    }

    if (showPassword) {
      // Check if password is empty when password field is shown
      if (!password) {
        setErrorMessage('Please type in your password');
        return;
      }

      // Capture before the request so response effects don't read a later edit.
      isSubmittingRef.current = true;
      submittedEmailRef.current = email;

      trackMethodChosen(LAST_AUTH_METHODS.PASSWORD);
      // Track password sign-in attempt
      trackEvent('signin-password-attempt', { referer });

      // Set loading state
      setIsLoading(true);

      // Use Redux action for password-based authentication
      signInWithPassword(email, password);
    } else {
      // Capture before the request so response effects don't read a later edit.
      isSubmittingRef.current = true;
      submittedEmailRef.current = email;

      trackMethodChosen(LAST_AUTH_METHODS.CODE);
      // Track passwordless sign-in attempt
      trackEvent('signin-passwordless-attempt', { referer });

      // Set loading state
      setIsLoading(true);

      // Use Redux action to request an auth code
      sendAuthCode(email);
    }
  };

  const togglePasswordField = () => {
    // Clear any existing error messages when switching modes
    setErrorMessage('');
    // Reset loading and success states when switching modes
    isSubmittingRef.current = false;
    setIsLoading(false);
    setIsSuccess(false);
    const nextShowPassword = !showPassword;
    // Toggle password field visibility
    setShowPassword(nextShowPassword);
    trackEvent('signin-method-chosen', {
      method: nextShowPassword
        ? LAST_AUTH_METHODS.PASSWORD
        : LAST_AUTH_METHODS.CODE,
      lastUsedShown: false,
      emailPrefilled,
      referer,
      source: 'toggle',
    });
  };

  const handleGoogleClick = () => {
    setLastAuth({ method: LAST_AUTH_METHODS.GOOGLE });
    trackMethodChosen(LAST_AUTH_METHODS.GOOGLE);
    trackEvent('signin-google-attempt', { referer });
  };

  const headingTitle = emailPrefilled ? 'Welcome back' : 'Sign in';
  const headingSubline = emailPrefilled
    ? 'Sign in to continue.'
    : 'Welcome back to Twine.';

  const primaryLabel = showPassword ? 'Sign in' : 'Continue with email';
  const primaryAriaLabel = primaryLastUsed
    ? `${primaryLabel} — last used`
    : primaryLabel;

  const footerLinks = (
    <>
      <p className={styles.footerLine}>
        New to Twine? <a href="/signup">Sign up for free</a>
      </p>
      <p className={styles.footerLine}>
        Hiring? <a href="/post-a-job">Post a project</a> for free and start
        getting proposals.
      </p>
    </>
  );

  return (
    <AuthShell>
      <Helmet title="Sign In" />

      <div className={styles.headingBlock}>
        <h1 className={styles.title}>{headingTitle}</h1>
        <p className={styles.subline}>{headingSubline}</p>
      </div>

      <form className={styles.formStack} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errorMessage) {
                setErrorMessage('');
              }
            }}
            aria-invalid={errorMessage ? true : undefined}
            aria-describedby={errorMessage ? 'signin-error' : undefined}
            className={styles.input}
            placeholder="you@example.com"
          />
        </div>

        {showPassword ? (
          <div className={styles.field}>
            <div className={styles.labelRow}>
              <label className={styles.label} htmlFor="password">
                Password
              </label>
              <button
                type="button"
                onClick={togglePasswordField}
                className={styles.labelRowLink}
              >
                Forgot? Email me a code
              </button>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errorMessage) {
                  setErrorMessage('');
                }
              }}
              aria-invalid={errorMessage ? true : undefined}
              aria-describedby={errorMessage ? 'signin-error' : undefined}
              className={styles.input}
              placeholder="Password"
            />
          </div>
        ) : null}

        {errorMessage ? (
          <div id="signin-error" className={styles.errorMessage} role="alert">
            {errorMessage}
          </div>
        ) : null}

        <div className={styles.actionWrap}>
          {primaryLastUsed ? (
            <span className={styles.lastUsedBadge} aria-hidden="true">
              Last used
            </span>
          ) : null}
          <ButtonWithLoader
            onClick={handleSubmit}
            loading={isLoading}
            success={isSuccess}
            disabled={isLoading}
            bsStyle="primary"
            className={`${styles.primaryButton} ${
              primaryLastUsed ? styles.primaryButtonLastUsed : ''
            }`}
            text={primaryLabel}
            id="signin-primary"
            aria-label={primaryAriaLabel}
          />
        </div>

        <p className={styles.microcopy}>
          {showPassword ? (
            <>
              Prefer not to type it?{' '}
              <button
                type="button"
                onClick={togglePasswordField}
                className={styles.labelRowLink}
              >
                Email me a one-time code
              </button>{' '}
              instead.
            </>
          ) : (
            <>
              No password needed — we&apos;ll send a one-time code to your
              inbox. Or{' '}
              <button
                type="button"
                onClick={togglePasswordField}
                className={styles.labelRowLink}
              >
                sign in with your password
              </button>
              .
            </>
          )}
        </p>

        <OrDivider />

        <GoogleContinueButton
          href="/signin/google"
          onClick={handleGoogleClick}
          lastUsed={googleLastUsed}
        />
      </form>

      <div className={styles.cardFooter}>{footerLinks}</div>
    </AuthShell>
  );
};

SignIn.propTypes = {
  location: PropTypes.object,
  signInWithPassword: PropTypes.func.isRequired,
  sendAuthCode: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { signInWithPassword, sendAuthCode })(
  SignIn
);
